"use client";

import React from "react";
import { Theme, ThemeProvider } from "../Theme/Theme";
import dayjs from "dayjs";
import updateLocale from "dayjs/plugin/updateLocale";
export interface DateLocaleConfig {
  startOfWeek?: number;
  weekdays?: string[];
  locale?: string;
}

type UpdateFunction = <K extends keyof WcWebUiContextType>(key: K, value: WcWebUiContextType[K]) => void;

export interface WcWebUiContextType {
  t: (key: string) => string;
  useTheme: () => Theme;
  children?: React.ReactNode;
  dateLocaleConfig?: DateLocaleConfig;
  mode: "light" | "dark";
}

const WebuiContext = React.createContext<
  | {
      state: WcWebUiContextType;
      update: UpdateFunction;
    }
  | undefined
>(undefined);

export const useWcWebUi = () => {
  const context = React.useContext(WebuiContext);
  if (context === null) {
    throw new Error("useWebui must be used within a WebuiProvider");
  }
  return context;
};

export const WcWebuiProvider = (props: WcWebUiContextType) => {
  const theme = props.useTheme();
  const [state, setState] = React.useState<WcWebUiContextType | null>(null);
  React.useEffect(() => {
    setState({
      dateLocaleConfig: props.dateLocaleConfig,
      t: props.t,
      useTheme: props.useTheme,
      mode: props.mode,
    });
    dayjs.extend(updateLocale);
    dayjs.updateLocale(props.dateLocaleConfig.locale || "nl", { weekStart: props.dateLocaleConfig.startOfWeek || 1 });
  }, [props]);

  const update = React.useCallback<UpdateFunction>((key, value) => {
    dayjs.extend(updateLocale);
    dayjs.updateLocale(state.dateLocaleConfig.locale || "nl", { weekStart: state.dateLocaleConfig.startOfWeek || 1 });
    setState((prevState) => {
      if (!prevState || prevState[key] === value) {
        return prevState;
      }
      return { ...prevState, [key]: value };
    });
  }, []);

  const contextValue = React.useMemo(
    () => ({
      state,
      update,
    }),
    [state, update]
  );

  return (
    <WebuiContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>{props.children}</ThemeProvider>
    </WebuiContext.Provider>
  );
};
